import { useState } from 'react';
import type { Person } from '../api/types';

interface Props {
  onSearch: (query: string) => Promise<Person[]>;
  onPick: (person: Person) => void;
  placeholder?: string;
  /** Что показать рядом с найденным человеком: «написать» или «добавить». */
  actionLabel?: string;
}

/**
 * Поиск человека по полному имени или логину.
 * <p>
 * Ищем по нажатию Enter, а не по мере ввода: совпадение требуется полное,
 * и запросы на каждую букву заведомо возвращали бы пустоту.
 */
export function PersonSearch({ onSearch, onPick, placeholder, actionLabel = 'написать' }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setBusy(true);
    try {
      setResults(await onSearch(trimmed));
    } catch {
      setResults([]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="search">
      <form onSubmit={submit}>
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setResults(null); }}
          placeholder={placeholder ?? 'Имя или логин полностью'}
          aria-label="Поиск человека по имени или логину"
          autoCapitalize="none"
          spellCheck={false}
        />
      </form>

      {busy && <p className="search-hint">Ищем…</p>}

      {!busy && results !== null && results.length === 0 && (
        <p className="search-hint">
          Никого не нашли. Имя или логин нужно указать полностью — поиска по части слова нет.
        </p>
      )}

      {!busy && results?.map((person) => (
        <button
          className="row"
          key={person.id}
          onClick={() => { onPick(person); setQuery(''); setResults(null); }}
        >
          <span className="body">
            <span className="name">{person.displayName}</span>
            <span className="preview">@{person.username}</span>
          </span>
          <span className="search-action">{actionLabel}</span>
        </button>
      ))}
    </div>
  );
}
